import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import AuthorProfileForm from './AuthorProfileForm'; 
import NewsletterSignup from "./NewsletterSignup";
import { logout } from '../../redux/actions/authActions';

const WritzyHome = () => {
  const authState = useSelector(state => state.authReducer);
  const { isLoggedIn } = authState;
  const [userName, setUserName] = useState(authState.user.name);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/writzy");
  };

  return (
    <div className="font-sans text-gray-800 bg-gray-100">
      {/* Header */}
      <header className="flex justify-between items-center px-6 py-4 bg-gradient-to-r from-gray-600 to-green-600 shadow-md sticky top-0 z-50">
        <div className="flex flex-col">
          <div className="text-4xl font-serif font-bold text-yellow-200 tracking-wider uppercase">
            WRITZY
          </div>
          <div className="text-sm text-gray-200 italic tracking-wide">
            Powered by Think5Whys
          </div>
        </div>

        {/* Desktop Menu */}
        <nav className="hidden md:flex space-x-6 font-medium text-gray-700">
          <a href="#books" className="text-white hover:text-yellow-200 transition">Books</a>
          <a href="#authors" className="text-white hover:text-yellow-200 transition">Authors</a>
          <a href="#publishers" className="text-white hover:text-yellow-200 transition">Publishers</a>
          <a href="#whyus" className="text-white hover:text-yellow-200 transition">Why Us</a>
        </nav>

        <div className="hidden md:flex items-center space-x-4">
          {isLoggedIn ? (
            <>
              <span className="text-yellow-200 font-medium">Welcome, {userName}</span>
              <button
                onClick={() => navigate(`/writzydashboard`)}
                className="w-9 h-9 flex items-center justify-center text-lg bg-green-500 text-white rounded-full hover:bg-green-600 transition"
                title="Dashboard"
              >
                🏠
              </button>
              <button
                onClick={handleLogout}
                className="w-9 h-9 flex items-center justify-center text-lg bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 text-sm border border-blue-500 text-blue-600 rounded-full hover:bg-blue-50 transition"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-full hover:bg-blue-700 transition"
              >
                Register
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <div className="md:hidden flex items-center justify-center w-24">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-3xl text-blue-600 focus:outline-none"
            aria-label="Toggle Menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white px-6 py-4 space-y-1 text-center shadow-md">
          {isLoggedIn && (
            <div className="flex flex-col items-center text-gray-800">
              <p className="text-green-600 font-medium">Welcome, {userName}</p>
              <div className="flex space-x-4 mt-2">
                <button
                  onClick={() => navigate(`/writzydashboard`)}
                  className="w-10 h-10 flex items-center justify-center text-lg bg-green-500 text-white rounded-full hover:bg-green-600 transition"
                >
                  🏠
                </button>
                <button
                  onClick={handleLogout}
                  className="w-10 h-10 flex items-center justify-center text-lg bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                >
                  ⏻
                </button>
              </div>
            </div>
          )}
          <a href="#books" className="block hover:text-yellow-200">Books</a>
          <a href="#authors" className="block hover:text-yellow-200">Authors</a>
          <a href="#publishers" className="block hover:text-yellow-200">Publishers</a>
          <a href="#whyus" className="block hover:text-yellow-200">Why Us</a>
          {!isLoggedIn && (
            <div className="flex justify-center space-x-4 pt-2">
              <button className="px-4 py-2 text-sm border border-blue-500 text-blue-600 rounded-full hover:bg-blue-50 transition">Login</button>
              <button className="px-4 py-2 text-sm bg-blue-600 text-white rounded-full hover:bg-blue-700 transition">Register</button>
            </div>
          )}
        </div>
      )}

      {/* Hero Section */}
      <section
        className="relative h-[75vh] sm:h-[85vh] bg-cover bg-center flex items-center justify-center text-white"
        style={{
          backgroundImage: "url('/assets/banners/writzy.webp')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60"></div>
        <div className="relative z-10 text-center px-4 sm:px-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 leading-tight">
            Where Ideas Turn Into Advanced Immersive Digital Books!
          </h1>
          <p className="text-xl text-white mb-6 font-bold">Think Beyond Writing — Publish with Power. Engage With Readers.</p>
          <a href="#vision" className="block">
            <button
              className="px-6 py-3 text-lg font-semibold bg-green-600 rounded-full hover:bg-green-700 transition hover:text-yellow-200"
            >
              Mission, Vision & Values
            </button>
          </a>
        </div>
      </section>

      {/* Books Section */}
      <section id="books" className="py-16 px-6 md:px-20 bg-green-100">
        <h2 className="text-3xl font-bold text-center mb-10 text-blue-700">Books</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              id: 1,
              title: "Digital Law & Legal Technology",
              description: "Learn cyber law, legal tech tools, digital evidence & automation.",
              img: "/api/downloads/thinkdscut/legend/courses/digital-law.jpg"
            },
            {
              id: 2,
              title: "Think5Whys Legal Reasoning Program",
              description: "Master critical thinking, case analysis, and strategic legal decision-making.",
              img: "/api/downloads/thinkdscut/legend/courses/legal-reasoning.jpg"
            },
            {
              id: 3,
              title: "CLE+ for Modern Legal Practice",
              description: "Modular credits in ADR, IP, ESG, and legal project management.",
              img: "/api/downloads/thinkdscut/legend/courses/cle-plus.jpg"
            }
          ].map((book) => (
            <div key={book.id} className="bg-white shadow-md hover:shadow-xl transition rounded-xl overflow-hidden">
              <img 
                src={book.img} 
                alt={book.title} 
                className="w-full h-52 object-cover"
              />
              <div className="p-5">
                <h3 className="text-xl font-semibold text-gray-800">{book.title}</h3>
                <p className="text-gray-600 mt-2">{book.description}</p>
                <button 
                  onClick={() => navigate(`/writzybooks/${book.id}`)}
                  className="mt-4 px-5 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 text-sm transition">
                  Book Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="authors" className="py-16 px-6 md:px-20 bg-gray-100">
        <h2 className="text-3xl font-bold text-center mb-10 text-blue-700">Authors</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              id: 1,
              name: "Ajay Srivastava",
              title: "Veteran Legal Counsel, Advocate, Supreme Court of India",
              img: "/api/downloads/thinkdscut/writzy/author/noauthorimage.jpg"
            },
            {
              id: 2,
              name: "Sanjeev Kumar",
              title: "Digital, AI & Cybersecurity Expert, Founder Think5Whys Global DeepTech Solutions",
              img: "/api/downloads/thinkdscut/writzy/author/noauthorimage.jpg"
            },
            {
              id: 3,
              name: "U. Swaminathan",
              title: "Cyberlaw Expert, Advocate, Madras High Court",
              img: "/api/downloads/thinkdscut/writzy/author/noauthorimage.jpg"
            }
          ].map((author) => (
            <div key={author.id} className="bg-white shadow-md hover:shadow-xl transition rounded-xl overflow-hidden text-center">
              <div className="flex justify-center items-center h-52 bg-gray-100">
                <img 
                  src={author.img} 
                  alt={author.name} 
                  className="h-full object-contain"
                />
              </div>
              <div className="p-5">
                <h3 className="text-xl font-semibold text-gray-800">{author.name}</h3>
                <p className="text-gray-600 mt-2">{author.title}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="publishers" className="py-16 px-6 md:px-20 bg-teal-100">
        <h2 className="text-3xl font-bold text-center mb-10 text-blue-700">Publishers</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              id: 1,
              name: "Sanjeev Kumar",
              title: "Digital, AI & Cybersecurity Expert, Founder Think5Whys Global DeepTech Solutions",
              img: "/api/downloads/thinkdscut/writzy/publisher/nopublisherimage.jpg"
            },
            {
              id: 2,
              name: "Gyan Ganga Publishing",
              title: "Renowned Indian Publisher of Educational and Legal Resources",
              img: "/api/downloads/thinkdscut/writzy/publisher/nopublisherimage.jpg"
            },
            {
              id: 3,
              name: "Harvard Law School Publishing",
              title: "Publisher of Prestigious Legal Texts and Research Papers",
              img: "/api/downloads/thinkdscut/writzy/publisher/nopublisherimage.jpg"
            },
          ].map((publisher) => (
            <div key={publisher.id} className="bg-white shadow-md hover:shadow-xl transition rounded-xl overflow-hidden text-center">
              <div className="flex justify-center items-center h-52 bg-gray-100">
                <img 
                  src={publisher.img} 
                  alt={publisher.name} 
                  className="h-full object-contain"
                />
              </div>
              <div className="p-5">
                <h3 className="text-xl font-semibold text-gray-800">{publisher.name}</h3>
                <p className="text-gray-600 mt-2">{publisher.title}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose WRITZY */}
      <section id="whyus" className="py-16 px-6 md:px-20 bg-gradient-to-r from-indigo-800 to-purple-600">
        <h2 className="text-3xl font-bold text-center mb-10 text-white">Why WRITZY?</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 text-center text-white">
          {[
            {
              title: "Immersive Reading Experience",
              emoji: "📖",
              desc: "Enable readers to read, listen, and annotate across languages with voice-to-text and AI note assistance."
            },
            {
              title: "Video Books & Multimedia",
              emoji: "🎥",
              desc: "Organize video-based learning materials alongside your books for a rich, interactive experience."
            },
            {
              title: "AI-Powered Engagement",
              emoji: "🤖",
              desc: "Leverage AI tools to enhance notes, quiz generation, content discovery, and smart reader recommendations."
            },
            {
              title: "Mentorship & Community",
              emoji: "🧑‍🏫",
              desc: "Mentor your audience directly, monitor progress, and interact with learning groups via Flyer on the Wall."
            },
            {
              title: "Assignments & Certification",
              emoji: "📜",
              desc: "Offer MCQs, assignments, and auto-generated certificates to drive credibility and learner success."
            },
            {
              title: "Daily Engagement Tools",
              emoji: "📆",
              desc: "Post daily ThinkTonic content, quizzes, and updates to build reader loyalty and boost visibility."
            },
            {
              title: "Insightful Analytics",
              emoji: "📊",
              desc: "Get deep insights on reader behavior, engagement patterns, and book performance with dynamic visual dashboards."
            },
            {
              title: "e-Commerce & Monetization",
              emoji: "💰",
              desc: "Sell books, stationery, and educational resources directly or via affiliate models within the WRITZY ecosystem."
            },
            {
              title: "Global Collaboration Gateway",
              emoji: "🌍",
              desc: "Partner with institutions and publishers worldwide to lead the evolution of immersive content delivery."
            }
          ].map((feature, idx) => (
            <div key={idx} className="bg-white/10 p-6 rounded-lg shadow-md backdrop-blur-md hover:bg-white/20 transition">
              <div className="text-5xl mb-4">{feature.emoji}</div>
              <h4 className="text-xl font-semibold mb-2">{feature.title}</h4>
              <p>{feature.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-sm text-gray-200 italic mb-6">
            We Are Inspired By <span className="text-yellow-300 font-semibold">Amazon Kindle</span>
          </p>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left text-white border border-gray-700 rounded-lg overflow-hidden">
              <thead className="bg-gradient-to-r from-gray-700 to-green-700 text-yellow-200">
                <tr>
                  <th className="px-4 py-3">Feature / Capability</th>
                  <th className="px-4 py-3 text-center">WRITZY</th>
                  <th className="px-4 py-3 text-center">Kindle</th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 text-gray-100">
                {[
                  ['Digital Reading', '✔ Immersive, multi-device reading', '✔ Optimized for Kindle devices + apps'],
                  ['Voice-to-Text & AI Notes', '✔ Integrated voice input, AI-enhanced annotation', '❌ Not natively available'],
                  ['Video Books & Multimedia', '✔ Video-based book formats and learning content', '❌ Primarily text/image-based content'],
                  ['Multilingual Support', '✔ Language translation, regional interface options', '✔ Some multilingual support'],
                  ['Think5Whys Tools', '✔ Built-in frameworks for deep thinking & problem solving', '❌ Not included'],
                  ['Assignments & Certification', '✔ Built-in assessment & auto-certification tools', '❌ Not supported'],
                  ['Daily Engagement', '✔ ThinkTonic content, quotes & quizzes', '❌ No daily engagement tools'],
                  ['Mentorship & Community', '✔ Mentoring + microlearning circles', '❌ Goodreads-level community'],
                  ['Reader Analytics', '✔ Real-time behavioral insights', '❌ Limited analytics for authors'],
                  ['e-Commerce & Affiliate Store', '✔ Sell books + stationery + affiliate earn', '✔ Kindle store only'],
                  ['AI Recommendations', '✔ Smart content & quiz suggestions', '✔ Personalized recommendations'],
                  ['Offline Access', '✔ Yes, with sync', '✔ Yes'],
                  ['Device Ecosystem', '🌐 Cross-platform (Web & Mobile)', '✔ Kindle Devices + App'],
                ].map(([feature, writzy, kindle], idx) => (
                  <tr key={idx} className="border-t border-gray-700">
                    <td className="px-4 py-3 font-semibold">{feature}</td>
                    <td className="px-4 py-3 text-green-300">{writzy}</td>
                    <td className="px-4 py-3 text-blue-300">{kindle}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </section>

      {/* Vision, Mission & Values */}
      <section id="vision" className="py-16 px-6 md:px-20 bg-gradient-to-r from-green-800 to-blue-600 text-white">
        <h2 className="text-2xl font-bold text-center mb-10">Vision, Mission & Values</h2>
        
        <div className="max-w-5xl mx-auto space-y-10">
          {/* Vision */}
          <div className="bg-white/10 p-6 rounded-lg shadow-md backdrop-blur-md hover:bg-white/20 transition">
            <h3 className="text-xl font-semibold mb-2">🌟 Vision</h3>
            <p className="text-lg">
              To revolutionize the way the world learns and thinks by creating a personalized, immersive, and inclusive 
              learning ecosystem that empowers every learner—regardless of their background—to grow, question, and succeed.
            </p>
          </div>

          {/* Mission */}
          <div className="bg-white/10 p-6 rounded-lg shadow-md backdrop-blur-md hover:bg-white/20 transition">
            <h3 className="text-xl font-semibold mb-2">🎯 Mission</h3>
            <ul className="list-disc list-inside text-lg space-y-2">
              <li>To build a dynamic learning and publishing platform that adapts to varied learner needs and preferences.</li>
              <li>To transform traditional content into interactive, collaborative, and AI-supported learning experiences.</li>
              <li>To empower authors and publishers with innovative tools to enhance content engagement and accessibility.</li>
              <li>To foster a global community dedicated to critical thinking, knowledge-sharing, and lifelong learning.</li>
            </ul>
          </div>

          {/* Core Values */}
          <div className="bg-white/10 p-6 rounded-lg shadow-md backdrop-blur-md hover:bg-white/20 transition">
            <h3 className="text-xl font-semibold mb-2">💡 Core Values</h3>
            <ul className="list-disc list-inside text-lg space-y-2">
              <li><strong>Integrity & Ethics</strong> — Upholding truth, fairness, and justice.</li>
              <li><strong>Innovation</strong> — Driving digital transformation in reading ecosystem.</li>
              <li><strong>Inclusivity</strong> — Making knowledge accessible for all.</li>
              <li><strong>Excellence</strong> — Delivering high-quality and relevant learning outcomes.</li>
              <li><strong>Collaboration</strong> — Building bridges between authors and readers.</li>
              <li><strong>Critical Inquiry</strong> — Empowering deeper problem-solving and analytical thinking.</li>
            </ul>
          </div>
        </div>
      </section>

      <AuthorProfileForm />

      <NewsletterSignup />

      {/* Footer */}
      <footer className="bg-gradient-to-r from-gray-600 to-green-600 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm">&copy; 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link to="/about-us" className="text-white hover:text-pink-400 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-pink-400 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-pink-400 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-pink-400 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WritzyHome;
